import {Formik, FormikProps} from "formik";
import * as React from "react";
import {Col, ControlLabel, FormControl, FormGroup, HelpBlock, OverlayTrigger, Tooltip} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {DatabaseType, DB_FILE_EXTENSION, DBConnectionConfig, EMPTY_ORACLE_DB_CONNECTION} from "../settings/model";
import * as yup from 'yup';
import {Button, Table, TableBody, TableCell, TableHead, TableRow} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import AddBoxIcon from "@material-ui/icons/AddBox";

type DatabaseConnectionFormOnChange =
    ((event: { target: { name: "databaseConnection"; value: DBConnectionConfig; } }) => void) &
    ((event: { target: { name: "isDatabaseConnectionValid"; value: boolean; } }) => void);

interface DatabaseConnectionFormOwnProps {
  onRef: (ref: DatabaseConnectionFormComponent) => void;
  databaseConnection: DBConnectionConfig;
  onChange: DatabaseConnectionFormOnChange;
  readOnly?: boolean;
  nameReadonly?: boolean;
  forceSrsEditable?: boolean;
}

type DatabaseConnectionFormProps = DatabaseConnectionFormOwnProps & WithApiProperties & InjectedIntlProps;

enum OracleIdType {
  SID = "SID",
  SERVICE = "SERVICE",
}

enum SQLPortInstance {
  PORT = "PORT",
  INSTANCE = "INSTANCE",
}

interface DatabaseConnectionFormState {
  initialValues: DBConnectionConfig & { oracleIdType: OracleIdType };
  oracleIdType: OracleIdType;
  sqlPortInstance: SQLPortInstance;
}

const MESSAGES = defineMessages({
  oracleOptionText: {
    id: "studio.settings.database-connections-form.oracle-option-text",
    defaultMessage: "Oracle",
  },
  postgisOptionText: {
    id: "studio.settings.database-connections-form.postgis-opton-text",
    defaultMessage: "PostGIS",
  },
  sqlserverOptionText: {
    id: "studio.settings.database-connections-form.sqlserver-option-text",
    defaultMessage: "SQL Server",
  },
  sid: {
    id: "studio.settings.database-connections-form.sid-label",
    defaultMessage: "SID",
  },
  serviceName: {
    id: "studio.settings.database-connections-form.service-name-label",
    defaultMessage: "Service name",
  },
  port: {
    id: "studio.settings.database-connections-form.port-label",
    defaultMessage: "Port",
  },
  instance: {
    id: "studio.settings.database-connections-form.instance-label",
    defaultMessage: "Instance",
  },
  connectionNameError: {
    id: "studio.settings.database-connections-form.connection-name-error",
    defaultMessage: "Connection name is a required field",
  },
  serverNameError: {
    id: "studio.settings.database-connections-form.server-name-error",
    defaultMessage: "Database host name is a required field",
  },
  serverPortError: {
    id: "studio.settings.database-connections-form.server-port-error",
    defaultMessage: "Database port number is a required field",
  },
  serverUsernameError: {
    id: "studio.settings.database-connections-form.server-username-error",
    defaultMessage: "Database user name is a required field",
  },
  serverPasswordError: {
    id: "studio.settings.database-connections-form.server-password-error",
    defaultMessage: "Database password is a required field",
  },
  oracleSidError: {
    id: "studio.settings.database-connections-form.oracle-sid-error",
    defaultMessage: "Oracle SID is a required field",
  },
  oracleServiceError: {
    id: "studio.settings.database-connections-form.oracle-service-error",
    defaultMessage: "Oracle service name is a required field",
  },
  databaseNameError: {
    id: "studio.settings.database-connections-form.database-name-error",
    defaultMessage: "Database name is a required field",
  },
  databaseTableError: {
    id: "studio.settings.database-connections-form.database-table-error",
    defaultMessage: "Database table name is a required field",
  },
  spatialColumnError: {
    id: "studio.settings.database-connections-form.spatial-column-error",
    defaultMessage: "Spatial column is a required field"
  },
  serverDefaultSrsError: {
    id: "studio.settings.database-connections-form.server-default-srs-error",
    defaultMessage: "SRS is a required field"
  },
  featureNameError: {
    id: "studio.settings.database-connections-form.feature-name-error",
    defaultMessage: "Feature name is a required field"
  },
  dataTypeError: {
    id: "studio.settings.database-connections-form.data-type-error",
    defaultMessage: "Data type is a required field"
  },
  serverInstanceError: {
    id: "studio.settings.database-connections-form.server-instance-error",
    defaultMessage: "Database instance is a required field",
  },
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },
});

const LABEL_COL = 3;
const CONTROL_COL = 8;
const HELP_COL = 1;

export class DatabaseConnectionFormComponent extends React.Component<DatabaseConnectionFormProps, DatabaseConnectionFormState> {
  private validationSchema: any = this.createValidationSchema();
  private formik: FormikProps<any>;

  constructor(props: DatabaseConnectionFormProps) {
    super(props);

    props.onRef(this);

    const initialDatabaseConnection = {...(props.databaseConnection || EMPTY_ORACLE_DB_CONNECTION)};
    const oracleIdType = !!props.databaseConnection?.oracleSid ? OracleIdType.SID : OracleIdType.SERVICE;
    const sqlPortInstance = !!props.databaseConnection?.mssqlInstanceName ? SQLPortInstance.INSTANCE
                                                                          : SQLPortInstance.PORT;

    this.state = {
      initialValues: {...initialDatabaseConnection, oracleIdType},
      oracleIdType,
      sqlPortInstance,
    };
  }

  private createValidationSchema() {
    const {intl} = this.props;

    if (this.props.readOnly) {
      return yup.object().shape({});
    }

    return yup.object().shape({
      connectionName: yup.string().required(intl.formatMessage(MESSAGES.connectionNameError)),
      serverName: yup.string().required(intl.formatMessage(MESSAGES.serverNameError)),
      serverPort: yup.number().when(['type'], {
        is: (type) => (type !== DatabaseType.SQLSERVER ||
                       (type === DatabaseType.SQLSERVER && this.state.sqlPortInstance === SQLPortInstance.PORT)),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.serverPortError)),
        otherwise: (schema) => schema.notRequired()
      }),
      mssqlInstanceName: yup.string().when(['type'], {
        is: (type) => (type === DatabaseType.SQLSERVER && this.state.sqlPortInstance === SQLPortInstance.INSTANCE),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.serverInstanceError)),
        otherwise: (schema) => schema.notRequired()
      }),
      serverUsername: yup.string().required(intl.formatMessage(MESSAGES.serverUsernameError)),
      serverPassword: yup.string().required(intl.formatMessage(MESSAGES.serverPasswordError)),
      oracleSid: yup.string().when(['type'], {
        is: (type) => (type === DatabaseType.ORACLE && this.state.oracleIdType === OracleIdType.SID),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.oracleSidError)),
        otherwise: (schema) => schema.notRequired()
      }),
      oracleServiceName: yup.string().when(['type'], {
        is: (type) => (type === DatabaseType.ORACLE && this.state.oracleIdType === OracleIdType.SERVICE),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.oracleServiceError)),
        otherwise: (schema) => schema.notRequired()
      }),
      mssqlDatabase: yup.string().when(['type'], {
        is: (type) => (type === DatabaseType.SQLSERVER),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.databaseNameError)),
        otherwise: (schema) => schema.notRequired()
      }),
      postgreSqlDatabase: yup.string().when(['type'], {
        is: (type) => (type === DatabaseType.POSTGIS),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.databaseNameError)),
        otherwise: (schema) => schema.notRequired()
      }),
      databaseTable: yup.string().required(intl.formatMessage(MESSAGES.databaseTableError)),
      spatialColumn: yup.string().required(intl.formatMessage(MESSAGES.spatialColumnError)),
      serverDefaultSrs: yup.string().required(intl.formatMessage(MESSAGES.serverDefaultSrsError)),
      databaseFeatures: yup.array().of(
          yup.object().shape({
            primaryFeature: yup.boolean().notRequired(),
            featureName: yup.string().required(intl.formatMessage(MESSAGES.featureNameError)),
            dataType: yup.string().required(intl.formatMessage(MESSAGES.dataTypeError)),
            displayName: yup.string().optional(),
          })
      )
    });
  }

  componentDidMount() {
    this.validateForm();
  }

  componentDidUpdate(prevProps: DatabaseConnectionFormProps) {
    if (this.props.databaseConnection.databaseFeatures.length === 0) {
      this.handleFeatureAdd();
    }
    if (this.props.databaseConnection !== prevProps.databaseConnection) {
      this.validateForm();
    }
  }

  render() {
    const {intl, readOnly, databaseConnection, forceSrsEditable} = this.props;
    const {initialValues, oracleIdType, sqlPortInstance} = this.state;

    return <Formik innerRef={ref => this.formik = ref} validationSchema={this.validationSchema}
                   initialValues={initialValues} onSubmit={() => null}>
      {({
          touched,
          errors,
        }) => (
          <React.Fragment>
            <FormGroup controlId="connectionName"
                       className={touched.connectionName && errors.connectionName ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.connection-name-label"
                                  defaultMessage="Connection name"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="connectionName" value={databaseConnection.connectionName}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                <HelpBlock>{touched.connectionName && errors.connectionName}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.connection-name.help"
                                    defaultMessage="{connectionName}.{extension} will be created and uploaded."
                                    values={{
                                      connectionName: databaseConnection.connectionName !== ""
                                                      ? databaseConnection.connectionName : "*",
                                      extension: DB_FILE_EXTENSION[databaseConnection.type]
                                    }}
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-name"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="databaseHost"
                       className={touched.serverName && errors.serverName ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.host-label"
                                  defaultMessage="Host"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="serverName" value={databaseConnection.serverName}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                <HelpBlock>{touched.serverName && errors.serverName}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.server-name.help"
                                    defaultMessage="Name of the database server"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-server-name"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>

            {databaseConnection.type !== "MSSql" &&
             <FormGroup controlId="databasePort"
                        className={touched.serverPort && errors.serverPort ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.settings.database-connections-form.port-label"
                                   defaultMessage="Port"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="serverPort" type="number" value={databaseConnection.serverPort}
                              onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                 <HelpBlock>{touched.serverPort && errors.serverPort}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.database-connection.port.help"
                                     defaultMessage="Port database is using"
                   />
                 </Tooltip>}>
                   <img src="../theme/assets/images/help_16.png" id="db-port"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>}


            {databaseConnection.type === DatabaseType.SQLSERVER && <FormGroup
                className={touched.mssqlInstanceName && errors.mssqlInstanceName || touched.serverPort &&
                           errors.serverPort ? "has-error" : "no-error"}>
              <Col sm={LABEL_COL}>
                <FormControl name="sqlPortInstance" value={sqlPortInstance} componentClass="select"
                             onChange={this.onChange}
                             style={{textAlignLast: "right"}} readOnly={readOnly}>
                  <option value={SQLPortInstance.PORT}>
                    {intl.formatMessage(MESSAGES.port)}
                  </option>
                  <option value={SQLPortInstance.INSTANCE}>
                    {intl.formatMessage(MESSAGES.instance)}
                  </option>
                </FormControl>
              </Col>

              <Col sm={CONTROL_COL}>

                {sqlPortInstance === SQLPortInstance.PORT &&
                 <FormControl name="serverPort" value={databaseConnection.serverPort}
                              onChange={this.onDatabaseConnectionChange}
                              readOnly={readOnly}/>
                }
                {sqlPortInstance === SQLPortInstance.INSTANCE &&
                 <FormControl name="mssqlInstanceName" value={databaseConnection.mssqlInstanceName}
                              onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                }
                <HelpBlock>{(touched.serverPort && errors.serverPort) ||
                            (touched.mssqlInstanceName && errors.mssqlInstanceName)}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  {sqlPortInstance === SQLPortInstance.INSTANCE &&
                   <FormattedMessage id="studio.add-data.database-connection.instance.help"
                                     defaultMessage="SQL Server Instance name"/>
                  }
                  {sqlPortInstance === SQLPortInstance.PORT &&
                   <FormattedMessage id="studio.add-data.database-connection.port.help"
                                     defaultMessage="Port database is using"/>
                  }
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-sql-instance-port"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            }
            <FormGroup controlId="databaseUser"
                       className={touched.serverUsername && errors.serverUsername ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.user-label"
                                  defaultMessage="User"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="serverUsername" value={databaseConnection.serverUsername}
                             onChange={this.onDatabaseConnectionChange} autoComplete="off" readOnly={readOnly}/>
                <HelpBlock>{touched.serverUsername && errors.serverUsername}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.user.help"
                                    defaultMessage="Connection User name"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-username"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="databasePassword"
                       className={touched.serverPassword && errors.serverPassword ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.password-label"
                                  defaultMessage="Password"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="serverPassword" type="password" value={databaseConnection.serverPassword}
                             onChange={this.onDatabaseConnectionChange} autoComplete="new-password"
                             readOnly={readOnly}/>
                <HelpBlock>{touched.serverPassword && errors.serverPassword}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.password.help"
                                    defaultMessage="Database Password"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-password"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>

            {databaseConnection.type === "Oracle" && <FormGroup
                className={touched.oracleSid && errors.oracleSid || touched.oracleServiceName &&
                           errors.oracleServiceName ? "has-error" : "no-error"}>
              <Col sm={LABEL_COL}>
                <FormControl name="oracleIdType" value={oracleIdType} componentClass="select" onChange={this.onChange}
                             style={{textAlignLast: "right"}} readOnly={readOnly}>
                  <option value={"SID"}>
                    {intl.formatMessage(MESSAGES.sid)}
                  </option>
                  <option value={"SERVICE"}>
                    {intl.formatMessage(MESSAGES.serviceName)}
                  </option>
                </FormControl>
              </Col>
              <Col sm={CONTROL_COL}>
                {oracleIdType === "SID" && <FormControl name="oracleSid" value={databaseConnection.oracleSid}
                                                        onChange={this.onDatabaseConnectionChange}
                                                        readOnly={readOnly}/>}
                {oracleIdType === "SERVICE" &&
                 <FormControl name="oracleServiceName" value={databaseConnection.oracleServiceName}
                              onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>}
                <HelpBlock>{(touched.oracleSid && errors.oracleSid) ||
                            (touched.oracleServiceName && errors.oracleServiceName)}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  {oracleIdType === "SID" && <FormattedMessage id="studio.add-data.database-connection.sid.help"
                                                               defaultMessage="Oracle database SID"/>
                  }
                  {oracleIdType === "SERVICE" && <FormattedMessage id="studio.add-data.database-connection.service.help"
                                                                   defaultMessage="Oracle database Service"/>
                  }

                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-sid"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>}


            {databaseConnection.type === "MSSql" &&
             <FormGroup controlId="sqlServerDatabaseName"
                        className={touched.mssqlDatabase && errors.mssqlDatabase ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.settings.database-connections-form.db-name-label"
                                   defaultMessage="Database"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="mssqlDatabase" value={databaseConnection.mssqlDatabase}
                              onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                 <HelpBlock>{touched.mssqlDatabase && errors.mssqlDatabase}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.database-connection.sql-database-name.help"
                                     defaultMessage="SQL Server Database name"
                   />
                 </Tooltip>}>
                   <img src="../theme/assets/images/help_16.png" id="db-sql-database-name"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>}

            {databaseConnection.type === "PostGIS" &&
             <FormGroup controlId="postgresDatabaseName"
                        className={touched.postgreSqlDatabase && errors.postgreSqlDatabase ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.settings.database-connections-form.db-name-label"
                                   defaultMessage="Database"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="postgreSqlDatabase" value={databaseConnection.postgreSqlDatabase}
                              onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                 <HelpBlock>{touched.postgreSqlDatabase && errors.postgreSqlDatabase}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.database-connection.pgs-name.help"
                                     defaultMessage="Postgres database name"
                   />
                 </Tooltip>}>
                   <img src="../theme/assets/images/help_16.png" id="db-pgs-name"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>}
            <FormGroup controlId="databaseTable"
                       className={touched.databaseTable && errors.databaseTable ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.database-table-label"
                                  defaultMessage="Table"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="databaseTable" value={databaseConnection.databaseTable}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                <HelpBlock>{touched.databaseTable && errors.databaseTable}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.table.help"
                                    defaultMessage="Database table"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-table"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="serverDefaultSrs"
                       className={touched.serverDefaultSrs && errors.serverDefaultSrs ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.data-srs-label"
                                  defaultMessage="Data SRS"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="serverDefaultSrs" value={databaseConnection.serverDefaultSrs}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly && !forceSrsEditable}/>
                <HelpBlock>{touched.serverDefaultSrs && errors.serverDefaultSrs}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.default-srs.help"
                                    defaultMessage="Data SRS"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-default-srs"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="bounds">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.bounds-label"
                                  defaultMessage="Bounds"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="bounds" value={databaseConnection.bounds}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.bounds.help"
                                    defaultMessage="Optional: Data Bounds"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-bounds"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="spatialColumn"
                       className={touched.spatialColumn && errors.spatialColumn ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.spatial-column-label"
                                  defaultMessage="Spatial Column"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="spatialColumn" value={databaseConnection.spatialColumn}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                <HelpBlock>{touched.spatialColumn && errors.spatialColumn}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.spatial-column.help"
                                    defaultMessage="Column name of the spatial data"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-spatial"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="geometryType"
                       className={touched.geometryType && errors.geometryType ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.geometry-column-type-label"
                                  defaultMessage="Geometry type"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="geometryType" value={databaseConnection.geometryType}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
                <HelpBlock>{touched.geometryType && errors.geometryType}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.geometry-type.help"
                                    defaultMessage="Optional: Geometry type of Spatial Column"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-geometry-type"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="databaseFeatures">
              <div>
                <Table
                    className="FeatureTable"
                    size="small"
                    aria-label="a dense table"
                    style={{display: "block"}}
                >
                  <colgroup>
                    <col style={{width: '10%'}}/>
                    <col style={{width: '30%'}}/>
                    <col style={{width: '30%'}}/>
                    <col style={{width: '30%'}}/>
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <label>
                          <FormattedMessage id="studio.database-connection-form.primary-feature"
                                            defaultMessage="Primary feature"/>
                        </label>
                      </TableCell>
                      <TableCell>
                        <label>
                          <FormattedMessage id="studio.database-connection-form.feature-name"
                                            defaultMessage="Feature name"/>
                        </label>
                      </TableCell>
                      <TableCell>
                        <label>
                          <FormattedMessage id="studio.database-connection-form.display-name"
                                            defaultMessage="Display name"/>
                        </label>
                      </TableCell>
                      <TableCell>
                        <label>
                          <FormattedMessage id="studio.database-connection-form.data-type"
                                            defaultMessage="Data type"/>
                        </label>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {databaseConnection.databaseFeatures.map((row, index) => {
                      return (
                          <TableRow key={`feature-key-${row.id}`}
                                    style={{verticalAlign: "top"}}>
                            <TableCell padding="none">
                              <input
                                  name="primaryFeature"
                                  id="primaryFeature"
                                  checked={row.primaryFeature}
                                  type="radio"
                                  onChange={(event) => this.handleFeatureInputChange(event, index)}
                              />
                            </TableCell>
                            <TableCell padding="none">
                              <FormGroup className={(touched &&
                                                     touched.databaseFeatures &&
                                                     touched.databaseFeatures[index] &&
                                                     index < Object.keys(touched.databaseFeatures).length &&
                                                     Object.keys(touched.databaseFeatures[index]).includes(
                                                         "featureName") &&
                                                     errors &&
                                                     errors.databaseFeatures &&
                                                     errors.databaseFeatures[index] &&
                                                     index < Object.keys(errors.databaseFeatures).length &&
                                                     Object.keys(errors.databaseFeatures[index]).includes(
                                                         "featureName")) ? "has-error"
                                                                         : "no-error"}>
                                <input
                                    name="featureName"
                                    id="featureName"
                                    value={row.featureName}
                                    onChange={(event) => this.handleFeatureInputChange(event, index)}
                                />
                                <HelpBlock>
                                  {
                                    touched &&
                                    touched.databaseFeatures &&
                                    touched.databaseFeatures[index] &&
                                    index < Object.keys(touched.databaseFeatures).length &&
                                    Object.keys(touched.databaseFeatures[index]).includes("featureName") &&
                                    errors &&
                                    errors.databaseFeatures &&
                                    errors.databaseFeatures[index] &&
                                    errors.databaseFeatures[index] ? (errors.databaseFeatures[index] as any).featureName
                                                                   : ""
                                  }
                                </HelpBlock>
                              </FormGroup>
                            </TableCell>
                            <TableCell padding="none">
                              <input
                                  name="displayName"
                                  id="displayName"
                                  value={row.displayName}
                                  onChange={(event) => this.handleFeatureInputChange(event, index)}
                              />
                            </TableCell>
                            <TableCell padding="none">
                              <FormGroup className={(touched &&
                                                     touched.databaseFeatures &&
                                                     touched.databaseFeatures[index] &&
                                                     index < Object.keys(touched.databaseFeatures).length &&
                                                     Object.keys(touched.databaseFeatures[index]).includes(
                                                         "dataType") &&
                                                     errors &&
                                                     errors.databaseFeatures &&
                                                     errors.databaseFeatures[index] &&
                                                     index < Object.keys(errors.databaseFeatures).length &&
                                                     Object.keys(errors.databaseFeatures[index]).includes("dataType"))
                                                    ? "has-error"
                                                    : "no-error"}>
                                <input
                                    name="dataType"
                                    id="dataType"
                                    value={row.dataType}
                                    onChange={(event) => this.handleFeatureInputChange(event, index)}
                                />
                                <HelpBlock>
                                  {
                                    touched &&
                                    touched.databaseFeatures &&
                                    touched.databaseFeatures[index] &&
                                    index < Object.keys(touched.databaseFeatures).length &&
                                    Object.keys(touched.databaseFeatures[index]).includes("dataType") &&
                                    errors &&
                                    errors.databaseFeatures &&
                                    errors.databaseFeatures[index] &&
                                    errors.databaseFeatures[index] ? (errors.databaseFeatures[index] as any).dataType
                                                                   : ""
                                  }
                                </HelpBlock>
                              </FormGroup>
                            </TableCell>
                            <TableCell padding="none">
                              <Button onClick={() => this.handleFeatureRemoveClick(index)}>
                                <ClearIcon/>
                              </Button>
                            </TableCell>
                          </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div style={{display: "flex", justifyContent: "space-between"}}>
                  <div style={{float: "left"}}>
                    <Button onClick={this.handleFeatureAdd}>
                      <AddBoxIcon onClick={this.handleFeatureAdd}/>
                      <FormattedMessage id="studio.database-connection-form.add" defaultMessage="ADD"/>
                    </Button>
                  </div>
                  <div style={{float: "right", marginRight: "40px", marginTop: "5px"}}>
                    <OverlayTrigger placement="top" overlay={<Tooltip
                        id="tooltip_help">
                      <FormattedMessage id="studio.add-data.database-connection.feature.help"
                                        defaultMessage="At least 1 feature must be added"
                      /> <br/> <br/>
                      <FormattedMessage id="studio.add-data.database-connection.feature-primary.help"
                                        defaultMessage="Primary Feature: One of the rows must be selected as the Primary Feature"
                      /> <br/> <br/>
                      <FormattedMessage id="studio.add-data.database-connection.feature-name.help"
                                        defaultMessage="Feature Name: The name of the column in the database"
                      /> <br/> <br/>
                      <FormattedMessage id="studio.add-data.database-connection.feature-display-name.help"
                                        defaultMessage="Display name (optional): The name to display"
                      /> <br/> <br/>
                      <FormattedMessage id="studio.add-data.database-connection.feature-type.help"
                                        defaultMessage="Data type: The regular SQL data type of the feature column"
                      />
                    </Tooltip>}>
                      <img src="../theme/assets/images/help_16.png" id="db-feature"
                           alt={intl.formatMessage(MESSAGES.HELP)}
                           onMouseOver={this.displayHelp}
                           onMouseLeave={this.displayHelp}/>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </FormGroup>
            <FormGroup controlId="selectionTables">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.selection-tables-label"
                                  defaultMessage="Selection tables"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="selectionTables" value={databaseConnection.selectionTables}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.selection-tables.help"
                                    defaultMessage="Optional: additional constraints to retrieve a subset of the table"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-selection-tables"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="selectionFilter">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.selection-filter-label"
                                  defaultMessage="Selection filter"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="selectionFilter" value={databaseConnection.selectionFilter}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.selection-filter.help"
                                    defaultMessage="Optional: additional constraints to retrieve a subset of the table"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-selection-filter"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="maxCacheSize">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.max-cache-size-label"
                                  defaultMessage="Max cache size"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="maxCacheSize" type="number" value={databaseConnection.maxCacheSize}
                             onChange={this.onDatabaseConnectionChange} readOnly={readOnly}/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.max-cache.help"
                                    defaultMessage="Optional: maximum cache size (expressed as number of objects, default is 100)"
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-max-cache"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>

            <FormGroup controlId="alwaysCloseConnection">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.always-close-connection-label"
                                  defaultMessage="Always close connection"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <input name="alwaysCloseConnection" checked={databaseConnection.alwaysCloseConnection}
                       onChange={this.onDatabaseConnectionChange} readOnly={readOnly} type="checkbox"/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.always-close-connection.help"
                                    defaultMessage="Only enable when using a connection pooling database driver."
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-always-close-connection"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>

            <FormGroup controlId="dropBeforeCreatingTable">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.settings.database-connections-form.drop-before-creating-table-label"
                                  defaultMessage="Drop before creating table"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <input name="dropBeforeCreatingTable" checked={databaseConnection.dropBeforeCreatingTable}
                       onChange={this.onDatabaseConnectionChange} readOnly={readOnly} type="checkbox"/>
              </Col>
              <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.database-connection.drop-table.help"
                                    defaultMessage="Be careful with this option: it will drop the existing table (if any) when creating a new table (instead of failing with an SQL exception)."
                  />
                </Tooltip>}>
                  <img src="../theme/assets/images/help_16.png" id="db-drop-table"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
          </React.Fragment>
      )}
    </Formik>;
  }

  private displayHelp = (event: any) => {
    if (event.type === "mouseover") {
      this.updateState({displayHelp: event.target.id});
    } else {
      this.updateState({displayHelp: ""});
    }
  }
  updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

  private onChange = (event: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;

    if (name === "oracleIdType") {
      if (value === OracleIdType.SID) {
        this.onDatabaseConnectionChange({target: {name: "oracleServiceName", value: ""}});
      }

      if (value === OracleIdType.SERVICE) {
        this.onDatabaseConnectionChange({target: {name: "oracleSid", value: ""}});
      }
    }

    if (name === "sqlPortInstance") {
      if (value === SQLPortInstance.PORT) {
        this.onDatabaseConnectionChange({target: {name: "serverPort", value: ""}});
      }
      if (value === SQLPortInstance.INSTANCE) {
        this.onDatabaseConnectionChange({target: {name: "mssqlInstanceName", value: ""}});
      }
    }

    this.setState({
      [name]: value,
    } as any);

    this.formik.setFieldTouched(name, true, false);
    this.formik.handleChange(event);
  }

  private onDatabaseConnectionChange = (event: any) => {
    const {onChange} = this.props;
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;

    const {databaseConnection} = this.props;
    const updatedDatabaseConnection = Object.assign({}, databaseConnection, {
      [name]: value
    });

    onChange({target: {name: "databaseConnection", value: updatedDatabaseConnection}});

    this.formik.setFieldTouched(name, true, false);
    this.formik.handleChange(event);
  }

  private validateForm() {
    const {onChange} = this.props;

    this.formik.validateForm()
        .then((errors) => {
          onChange({target: {name: "isDatabaseConnectionValid", value: Object.keys(errors).length === 0}});
        });
  }

  public showAllErrors() {  //Used in AddDataDialog.tsx
    this.formik.submitForm();
  }

  private handleFeatureAdd = () => {
    const {onChange, databaseConnection} = this.props;
    const databaseFeatures = [...databaseConnection.databaseFeatures];
    const newFeature = {
      id: (databaseFeatures.length) === 0 ? 0 : databaseFeatures[databaseFeatures.length - 1].id + 1,
      primaryFeature: databaseFeatures.length === 0,
      featureName: "",
      displayName: "",
      dataType: ""
    };

    const newDatabaseFeatures = [
      ...databaseFeatures,
      newFeature,
    ];

    const updatedDatabaseConnection = Object.assign({}, databaseConnection, {
      databaseFeatures: newDatabaseFeatures
    });
    onChange({target: {name: "databaseConnection", value: updatedDatabaseConnection}});
    this.formik.handleChange({target: {name: "databaseFeatures", value: newDatabaseFeatures}});
  };

  private handleFeatureInputChange = (event, index) => {
    const {onChange, databaseConnection} = this.props;
    const {name, value} = event.target;
    const databaseFeatures = [...databaseConnection.databaseFeatures];

    if (event.target.type === "radio") {
      databaseFeatures.forEach((feature) => {
        feature[name] = false;
      })
      databaseFeatures[index][name] = true;
    } else {
      databaseFeatures[index][name] = value;
    }

    const updatedDatabaseConnection = Object.assign({}, databaseConnection, {
      databaseFeatures
    });
    onChange({target: {name: "databaseConnection", value: updatedDatabaseConnection}});
    this.formik.setFieldTouched(`databaseFeatures[${index}][${name}]`, true, false);
    this.formik.handleChange({target: {name: "databaseFeatures", value: databaseFeatures}});
  };

  private handleFeatureRemoveClick = (i) => {
    const {onChange, databaseConnection} = this.props;
    const databaseFeatures = [...databaseConnection.databaseFeatures];
    const primaryDeleted = databaseFeatures[i].primaryFeature;

    databaseFeatures.splice(i, 1);
    if (primaryDeleted && databaseFeatures.length > 0) {
      databaseFeatures[0].primaryFeature = true;
    }
    const updatedDatabaseConnection = Object.assign({}, databaseConnection, {
      databaseFeatures
    });
    onChange({target: {name: "databaseConnection", value: updatedDatabaseConnection}});
  };
}

export const DatabaseConnectionForm = WithApi(injectIntl(DatabaseConnectionFormComponent));